import React from 'react';
import PropTypes from 'prop-types';
import styled, { keyframes } from 'styled-components';

import HFIcon from '../icons';

// =============================================================================
// OptimizeOverlay — overlay de progresso que TRAVA o board enquanto a tripla
// selecionada (data+período+base) está sendo otimizada. Cobre o stage inteiro
// (mapa + painéis + dock + drawer) e captura todos os cliques, então nada do
// board é operável durante a otimização. Fica DENTRO do .hf-stage, abaixo da
// toolbar — por isso data/período/base seguem navegáveis (a toolbar não é
// coberta). Mudar de tripla esconde o overlay (a otimização segue em 2º plano).
//
// % real vem do backend (status.progress). Enquanto null (1º tick ou deploy
// antigo), mostra um indeterminado "Iniciando…" — sem número encenado.
// =============================================================================

const indeterminate = keyframes`
   0%   { left: -40%; }
   100% { left: 100%; }
`;

const pulse = keyframes`
   0%, 100% { opacity: 0.55; }
   50%      { opacity: 1; }
`;

const Backdrop = styled.div`
   position: absolute;
   inset: 0;
   z-index: 9000;
   display: flex;
   align-items: center;
   justify-content: center;
   background: rgba(15, 15, 12, 0.55);
   backdrop-filter: blur(2px);
`;

const Card = styled.div`
   width: min(380px, calc(100% - 48px));
   background: #ffffff;
   border-radius: 14px;
   box-shadow: 0 16px 48px rgba(0, 0, 0, 0.32);
   padding: 22px 24px;
   display: flex;
   flex-direction: column;
   gap: 14px;
   color: #1f1f18;
`;

const Head = styled.div`
   display: flex;
   align-items: center;
   gap: 10px;

   .bolt {
      display: inline-flex;
      color: #2563eb;
      animation: ${pulse} 1.4s ease-in-out infinite;
   }

   .ttl {
      font-size: 0.98rem;
      font-weight: 700;
   }
`;

const Tripla = styled.div`
   font-size: 0.78rem;
   color: #6b6b62;
   font-variant-numeric: tabular-nums;
`;

const PercentRow = styled.div`
   display: flex;
   align-items: baseline;
   justify-content: space-between;

   .pct {
      font-size: 1.9rem;
      font-weight: 800;
      font-variant-numeric: tabular-nums;
      line-height: 1;
   }

   .state {
      font-size: 0.8rem;
      font-weight: 600;
      color: #2563eb;
   }
`;

const Track = styled.div`
   position: relative;
   height: 8px;
   border-radius: 999px;
   background: rgba(37, 99, 235, 0.14);
   overflow: hidden;
`;

const Fill = styled.div`
   position: absolute;
   inset: 0 auto 0 0;
   width: ${({ $pct }) => $pct}%;
   border-radius: 999px;
   background: #2563eb;
   transition: width 0.5s ease;
`;

const Indeterminate = styled.div`
   position: absolute;
   top: 0;
   bottom: 0;
   width: 40%;
   border-radius: 999px;
   background: #2563eb;
   animation: ${indeterminate} 1.2s ease-in-out infinite;
`;

const Note = styled.div`
   font-size: 0.74rem;
   line-height: 1.5;
   color: #8b8b82;
`;

const STATE_LABEL = {
   queued: 'Na fila…',
   processing: 'Processando…',
};

export default function OptimizeOverlay({
   state = 'processing',
   progress = null,
   tripla = null,
}) {
   const hasPct = typeof progress === 'number';
   const stateLabel = STATE_LABEL[state] || 'Processando…';

   return (
      <Backdrop
         // Captura cliques/scroll: nada do board reage durante a otimização.
         onClick={(e) => e.stopPropagation()}
         role="alertdialog"
         aria-busy="true"
         aria-label="Otimização em andamento"
      >
         <Card>
            <Head>
               <span className="bolt">{HFIcon.bolt}</span>
               <span className="ttl">Otimizando roteirização</span>
            </Head>

            {tripla && (
               <Tripla>
                  {tripla.dateLabel} · {tripla.periodLabel} · {tripla.baseLabel}
               </Tripla>
            )}

            <PercentRow>
               <span className="pct">{hasPct ? `${progress}%` : '—'}</span>
               <span className="state">{stateLabel}</span>
            </PercentRow>

            <Track>
               {hasPct ? (
                  <Fill $pct={progress} />
               ) : (
                  <Indeterminate />
               )}
            </Track>

            <Note>
               A otimização roda em segundo plano. Você pode trocar de data,
               período ou base — o board volta quando concluir.
            </Note>
         </Card>
      </Backdrop>
   );
}

OptimizeOverlay.propTypes = {
   state: PropTypes.string,
   progress: PropTypes.number,
   tripla: PropTypes.shape({
      dateLabel: PropTypes.string,
      periodLabel: PropTypes.string,
      baseLabel: PropTypes.string,
   }),
};
